/**
* @author 贝才[beica1@outook.com]
* @date 2021/3/3
* @description
*   Symbol.vue of WeTrade
*/
<template>
<div class="fill df-col p-r">
  <div class="mt-10 d-f px-20 py-12">
    <TheSymbolMarketState :symbol="symbol" />
    <div class="f-md watch_btn c-title flex-1" :class="{'c-tip': added}" @click="toggle">
      <t path="symbol_24" class="mr-4">Watchlist</t>
      <Donut v-if="progress" sm />
      <Icon v-else :name="added ? 'minus' : 'plus'" sm />
    </div>
  </div>
  <template v-if="symbol">
    <TheChart v-if="fullscreen" :code="symbol.code" class="fullscreen"
      @fullscreen="fullscreen = false" />
    <TheChart v-else :code="symbol.code" class="chart flex-1" @fullscreen="fullscreen = true" />
  </template>
  <div v-else class="flex-1"></div>
  <TheMarketClosedTip v-if="isClosed" :symbol="symbol" />
  <!--<DemoAccountTip />-->
  <TheSymbolActions :symbol="symbol" />

  <div v-if="showSpecialTip" class="x2-tip br-lg d-f p-8 pb-0">
    <Icon name="help" sm style="color: var(--color-asst);" />
    <div class="px-8 flex-1">
      <t v-if="sepcialType===ESpecialType.DOUBLE"
        path="symbol_104" as="div"
        multiline #="{td}" custom :args="{code: code}">
        <p class="c-white mb-8" v-for="(p, index) in td" :key="index">{{p}}</p>
      </t>
      <t v-if="sepcialType===ESpecialType.REVERSE"
        path="symbol_105" as="div"
        multiline #="{td}" custom :args="{code: code, origincode: code.replace('INV', '')}">
        <p class="c-white mb-8" v-for="(p, index) in td" :key="index">{{p}}</p>
      </t>
    </div>
    <Icon name="close"
      style="background-color:var(--color-white);
      border-radius: 50%; height:24px;width: 24px;
      cursor: pointer;"
      @click="handleCloseTip" />
  </div>
</div>
</template>

<script lang="ts">
import Icon from '@/components/Icon.vue'
import { events, keymap } from '@/config'
import { cachePage, cancelCachePage } from '@/features/dynamicAlive'
import TheChart from '@/modules/market/components/chart/TheChart.vue'
import TheMarketClosedTip from '@/modules/market/components/TheMarketClosedTip.vue'
import TheSymbolActions from '@/modules/market/components/TheSymbolActions.vue'
import TheSymbolMarketState from '@/modules/market/components/TheSymbolMarketState.vue'
import { useQuote, useWatchState } from '@/modules/market/market'
import { MarketState } from '@/types'
import Donut from 'common/loading/Donut.vue'
import { on } from 'essential/tools/event'
import { computed, defineComponent, shallowRef } from 'vue'

import { checkIsSpecial, ESpecialType } from '@/modules/market/market.api'
import { localGet, localSet } from 'essential/store/localStore'

let FROM_PATH = ''

export default defineComponent({
  name: 'Symbol',
  components: {
    Donut,
    Icon,
    TheChart,
    TheMarketClosedTip,
    TheSymbolMarketState,
    TheSymbolActions,
  },
  beforeRouteEnter (to, from) {
    FROM_PATH = FROM_PATH === '' ? from.path : FROM_PATH
  },
  beforeRouteLeave (to) {
    if (to.path === FROM_PATH || to.meta.noCache) {
      cancelCachePage('Symbol')
    } else {
      cachePage('Symbol')
    }
  },
  setup () {
    const code = shallowRef('')
    const symbol = useQuote(code, true)
    const fullscreen = shallowRef(false)

    const showSpecialTip = shallowRef(false)
    const sepcialType = shallowRef(ESpecialType.NORMAL)

    const haveShow = localGet(keymap.guide.specialTips) ? (localGet(keymap.guide.specialTips) as string).split(',') : ['0', '0']

    const handleCheck = (code: string) => {
      checkIsSpecial({
        contract: code,
      }).then(res => {
        sepcialType.value = res.specialIdentification
        if (haveShow[sepcialType.value - 1] === '0') {
          showSpecialTip.value = true
        }
      })
    }

    const handleCloseTip = () => {
      haveShow[sepcialType.value - 1] = '1'
      localSet(keymap.guide.specialTips, haveShow.join().toString())
      showSpecialTip.value = false
    }

    on(events.symbolChange, (newCode, forDefault) => {
      if ((forDefault && !code.value) || !forDefault) {
        code.value = newCode
        showSpecialTip.value = false
        handleCheck(newCode)
      }
    })

    const { toggle, added, progress } = useWatchState(code)

    return {
      isClosed: computed(() => symbol.value?.isClosed === MarketState.CLOSED),
      symbol,
      added,
      progress,
      fullscreen,
      toggle,
      code,
      showSpecialTip,
      handleCloseTip,
      sepcialType,
      ESpecialType,
    }
  },
})
</script>

<style lang="scss" scoped>
.watch_btn {
  text-align: right;
  background: none;
  min-width: 80px;
  padding: 0;

  .icon {
    font-size: 12px;
    border-radius: 50%;
    border: 1px solid;
  }
}

.fullscreen {
  z-index: 1;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.x2-tip{
  position: absolute;
  top: var(--page-header-height);
  left: 16px;
  background-color: rgba(24, 29, 36, 0.9);
  width: calc(100% - 32px);
}
</style>
